import { forwardRef } from 'react'
import { Button, ButtonProps } from '@chakra-ui/react'
import {
  CREATE_BUTTON_GRADIENT,
  CREATE_BUTTON_GRADIENT_HOVER,
  DARK_PILL_BUTTON,
  DARK_TEXT_PRIMARY
} from 'constants/dark-tokens'

/**
 * The RallyPD primary action — the blue gradient pill used for the confirming
 * button of a dark modal, drawer or toolbar.
 *
 * A component rather than another props object because the shape is fixed and
 * repeated across every dark surface: 32px tall, 24px radius, the `--Button-
 * Stroke` hairline and `--Button-Gradient-2` fill. Restating that per call site
 * is how the modals drifted apart in the first place.
 *
 * Every interactive background is declared here on purpose: Chakra's `solid`
 * variant sets a light `_hover` (`gray.200`) and the theme's hover beats a plain
 * `bg` style prop, so a button that only sets `bg` flashes white on hover.
 *
 * Props are spread last, so size, width or colour can still be overridden for a
 * one-off without forking the component.
 */
const PrimaryButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <Button
    ref={ref}
    variant="solid"
    {...DARK_PILL_BUTTON}
    background={CREATE_BUTTON_GRADIENT}
    color={DARK_TEXT_PRIMARY}
    _hover={{ background: CREATE_BUTTON_GRADIENT_HOVER }}
    _active={{ background: CREATE_BUTTON_GRADIENT_HOVER }}
    _disabled={{ opacity: 0.5, background: CREATE_BUTTON_GRADIENT }}
    _focus={{ boxShadow: 'none' }}
    _focusVisible={{ boxShadow: 'none' }}
    {...props}
  />
))

PrimaryButton.displayName = 'PrimaryButton'

export default PrimaryButton
